import {SearchDataSettingsPaginationInfo, UseSearchResult} from "../use-search";
import {ReactNode, useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../use-is-mounted";
import {DataGridColumn, DataGridColumnTypes, DataGridRow, GetExportInfoFunc} from "../../../packages/data-grid";
import {ReportingApiResponse} from "../../../core/services/api/type-declerations/response";
import {DefaultPaginationInfo} from "../../../core/constants/enums";
import {exportCSVFile, formatMoney} from "../../../core/services/utils/utils";
import ToastService from "../../../core/services/toast";
import DateUtils from "../../../core/services/date-utils";
import {ApiResponse} from "../../../@bizkeytech/base/core";


// unique symbol named continue marker
const ContinueMarker = Symbol('continueMarker');

export interface UseReportingSearchUtilsDataType {
    id?: string | number;

    [p: string]: any,
}

export interface UseReportingSearchUtilsArg<DataType extends UseReportingSearchUtilsDataType> {
    savedDataGridColumns: Array<DataGridColumn>,
    fetchEntries: (args: UseSearchResult, isExporting?: boolean) => ReportingApiResponse<DataType> | ApiResponse<DataType[]>,
    searchData: UseSearchResult,
    exportingChunkSize?: number,
    formatExportedCell?: (cell: DataType[keyof DataType], column: DataGridColumn, continueMarker: typeof ContinueMarker) => null | undefined | string | typeof continueMarker,
    exportingTitle?: string,
    getDataGridCell?: (row: DataType, column: DataGridColumn, continueMarker: typeof ContinueMarker) => ReactNode | typeof continueMarker,
    data: Array<DataType>,
    getEntryKey?: (entry: DataType) => string | number,
}

export type UseReportingSearchUtilsResult<DataType extends Record<string, any> = Record<string, any>> = {
    exporting: boolean,
    getExportInfo: GetExportInfoFunc,
    getAllData: () => Promise<Array<DataType>>,
    exportData: () => Promise<void>,
    dataGridRows: Array<DataGridRow>,
}

/**
 * A hook that provides common functions for reporting search.
 *
 * @param searchData                    The search data.
 * @param fetchEntries                  The function that fetches the entries.
 * @param savedDataGridColumns          The data grid columns.
 * @param exportingChunkSize            The exporting chunk size.
 * @param formatExportedCell            The function that formats the exported cell.
 * @param exportingTitle                the title of the exported data
 * @param getDataGridCell               The function that formats the data grid cell.
 * @param data                          The data grid rows.
 * @param getEntryKey                   The function that gets the entry key.
 */
const useReportingSearchUtils = <DataType extends UseReportingSearchUtilsDataType = UseReportingSearchUtilsDataType>(
    {
        searchData,
        fetchEntries,
        savedDataGridColumns,
        getDataGridCell,
        exportingChunkSize,
        formatExportedCell,
        exportingTitle,
        data,
        getEntryKey,
    }: UseReportingSearchUtilsArg<DataType>): UseReportingSearchUtilsResult => {
    const [exporting, setExporting] = useState(false);
    const isMounted = useIsMounted();

    /**
     * With each change in the [exporting] condition of the state:
     * - closes or opens the exporting dialog.
     */
    useEffect(() => {
        if (exporting) {
            window.partner?.openExportingDialog?.();
        } else {
            window.partner?.closeExportingDialog?.();
        }
    }, [exporting]);

    /**
     * Fetches all the entries from the server based on the user's filters.
     */
    const getAllData = useCallback<UseReportingSearchUtilsResult['getAllData']>(async () => {
        const _paginationInfo: SearchDataSettingsPaginationInfo = {
            length: searchData.paginationInfo!.length,
            pageSize: exportingChunkSize ?? 100,
            currentPage: 1,
            sizes: DefaultPaginationInfo.sizes,
        };
        let entries: Array<DataType> = [];
        while (searchData.paginationInfo!.length > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            const items = (Array.isArray(res?.data) ? res.data : res?.data?.items) ?? [];
            if (!res?.resultFlag)
                return [];

            if (!items.length)
                break;

            entries.push(...items);
            if (!isMounted())
                return [];
            _paginationInfo.currentPage++;
        }
        return entries;
    }, [exportingChunkSize, fetchEntries, isMounted, searchData])

    /**
     * Fetches the entries for exporting purposes from the server based on the user's filters.
     */
    const getExportInfo = useCallback<UseReportingSearchUtilsResult['getExportInfo']>(async () => {
        const entries = await getAllData();
        if (!entries.length)
            return;

        const body = entries
            .map(entry => {
                const res: Record<string, string> = {};
                const keys = Object.keys(entry);
                for (const key of keys) {
                    const column = savedDataGridColumns.find(e => e.name === key);
                    if (!column)
                        continue;
                    let title: string | undefined | null = typeof column.title === 'function' ? column.title()?.toString() : column.title;
                    if (typeof title !== 'string' || !title) {
                        title = column.name;
                    }
                    const cell = formatExportedCell?.(entry[key], column, ContinueMarker);
                    if (!formatExportedCell || cell === ContinueMarker) {
                        switch (column.type) {
                            case DataGridColumnTypes.date:
                                res[title] = DateUtils.serverToLocal(entry[key]).format(DateUtils.dateFormat);
                                break;
                            case DataGridColumnTypes.dateTime:
                                res[title] = DateUtils.serverToLocal(entry[key]).format(DateUtils.dateTimeFormat);
                                break;
                            case DataGridColumnTypes.money:
                                let parsed = parseFloat(entry[key]);
                                if (Number.isNaN(parsed)) {
                                    res[title] = "--";
                                } else {
                                    res[title] = formatMoney(parsed) ?? '--';
                                }
                                break;
                        }
                        res[title] = entry[key] ?? "--";
                    } else {
                        res[title] = cell ?? '--';
                    }
                }
                if (Object.keys(res).length === 0) {
                    return undefined;
                }
                return res;
            })
            .filter(e => e !== undefined) as Array<Record<string, string>>;
        return {
            title: exportingTitle ?? document.title.concat(' - Exported Data').concat(` - ${DateUtils.localNow().format(DateUtils.dateTimeFormat)}`),
            body: body,
        }
    }, [getAllData, exportingTitle, savedDataGridColumns, formatExportedCell])

    /**
     * Exports the data.
     */
    const exportData = useCallback<UseReportingSearchUtilsResult['exportData']>(async () => {
        setExporting(true);
        const exportInfo = await getExportInfo();
        if (!isMounted())
            return;
        setExporting(false);
        if (!exportInfo?.body?.length) {
            ToastService.warning({title: "Export Failed", message: "No data to export!"});
            return
        }
        exportCSVFile(exportInfo.body, exportInfo.title);
    }, [getExportInfo, isMounted])

    /**
     * The data grid rows to be displayed.
     */
    const dataGridRows = useMemo<UseReportingSearchUtilsResult['dataGridRows']>(() => {
        return data.map(dataEntry => {
            const res: Record<string, any> = {};
            for (const column of savedDataGridColumns) {
                const cell = getDataGridCell?.(dataEntry, column, ContinueMarker);
                if (!getDataGridCell || cell === ContinueMarker) {
                    res[column.name] = dataEntry[column.name as keyof DataType];
                } else {
                    res[column.name] = cell;
                }
            }
            return {
                key: (dataEntry.id ?? getEntryKey?.(dataEntry))!,
                data: dataEntry,
                cells: res,
            }
        });
    }, [data, getDataGridCell, getEntryKey, savedDataGridColumns])


    return useMemo(() => ({
        exporting,
        exportData,
        getAllData,
        getExportInfo,
        dataGridRows
    }), [
        exporting,
        exportData,
        getAllData,
        getExportInfo,
        dataGridRows
    ])
}

export default useReportingSearchUtils;
